import './styles/VuetifyLayers.css';
import '@fontsource-variable/noto-sans-display';
import { createZxcvbn } from '@/plugins/zxcvbn';
import i18next from 'i18next';
import I18NextVue from 'i18next-vue';
import { createApp } from 'vue';
import App from './App.vue';
import { loadI18Next } from './locales/i18n';
import { routerInstance } from './router/router';
import { pinia } from './stores/pinia';
import { useSystemStore } from './stores/systemStore';
import { vuetify } from './styles/vuetify';
import 'vuetify/styles';
import './styles/PtwLayer.scss';
import './styles/highcharts.scss';
import { maskito } from '@maskito/vue';
import { getDeviceStatusHubConnectionSignalR } from './components/deviceStatus/utils/useDeviceStatusSignalR';
import { getWorkflowCommunicationHubConnectionSignalR } from './components/measurement/utils/useMeasurement';
import { DeviceStatusHubKey } from '@/components/deviceStatus/utils/severity';
import { WorkflowCommunicationHubKey } from './components/measurement/utils/types';
import {
  EntityEventHubKey,
  getEntityEventHubConnectionSignalR,
} from './views/jobOccurrences/utils/useEntityEventHub';
import { ENABLE_MOCKING } from './env';
import { Level, trackException } from './telemetry/appInsights';
import { useAuthenticationStore } from './stores/authenticationStore';

// include Mocking only for development
if (ENABLE_MOCKING) {
  const { worker } = await import('./mocks/browser');
  await worker.start({ onUnhandledRequest: 'bypass' });
}

const app = createApp(App).use(vuetify).use(pinia).use(createZxcvbn());

function setupWindowAndRuntimeErrorHandlers() {
  app.config.errorHandler = (err: any, _instance: any, info: any) => {
    trackException(
      err instanceof Error ? err : new Error(String(err)),
      {
        detail: 'app.config.errorHandler',
        info,
      },
      Level.Error,
    );
  };

  window.addEventListener('unhandledrejection', (event) => {
    trackException(
      event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
      {
        detail: 'unhandledrejection',
      },
      Level.Error,
    );
  });
}

startup().then(() => {
  app
    // use router a bit later, because router-redirects need access to pinia-permissions and cannot be wait for `startupFinished`
    .use(routerInstance)
    // wait until user language is loaded
    .use(I18NextVue, { i18next })
    .directive('maskito', maskito)
    .provide(DeviceStatusHubKey, getDeviceStatusHubConnectionSignalR())
    .provide(WorkflowCommunicationHubKey, getWorkflowCommunicationHubConnectionSignalR())
    .provide(EntityEventHubKey, getEntityEventHubConnectionSignalR())
    .mount('#app');
});

// initial startup sequence for actions that have to be done before mounting the app
// attention: router might not be ready yet
async function startup() {
  setupWindowAndRuntimeErrorHandlers();
  // needs to be done first to load configurations for i18next (languages and useLiveTranslations)
  await useSystemStore().fetchAllData();
  await loadI18Next();
  await useAuthenticationStore().startup();
}
